<template>
  <Button
    :class="triggerClasses"
    @click.prevent.stop="handleClick"
    :aria-label="ariaLabel"
    :disabled="!chatbot.store.isInitialized"
    variant="default"
    size="icon"
    type="button"
  >
    <div class="flex items-center justify-center w-full h-full relative">
      <Avatar
        v-if="chatbot.store.brand?.logoUrl && chatbot.store.isInitialized"
        class="w-12 h-12 sidd-avatar"
      >
        <AvatarImage
          :src="chatbot.store.brand.logoUrl"
          :alt="chatbot.store.brand?.name"
          class="w-full h-full object-cover rounded-full"
          @error="handleLogoError"
        />
        <AvatarFallback class="w-full h-full bg-transparent rounded-full">
          <MessagesSquare
            class="sidd-chat-icon"
            :style="{ color: contrastColor }"
          />
        </AvatarFallback>
      </Avatar>

      <MessagesSquare
        v-else
        class="sidd-chat-icon"
        :style="{
          color: contrastColor,
          opacity: chatbot.store.isInitialized ? 1 : 0.6,
        }"
      />
    </div>

    <div
      v-if="hasUnreadMessages"
      class="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] rounded-full text-xs font-semibold flex items-center justify-center px-1 shadow-md border-2 sidd-notification"
    >
      {{ unreadCount && unreadCount > 9 ? '9+' : unreadCount }}
    </div>
  </Button>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Button } from '@/lib/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from '@/lib/ui/avatar'
import { MessagesSquare } from 'lucide-vue-next'
import { useChatbot } from '@/composables/useChatbot'
import { logger } from '@/utils/logger'
import type { IChatTriggerProps, IChatTriggerEmits } from './types'

const props = withDefaults(defineProps<IChatTriggerProps>(), {
  isOpen: false,
  isHidden: false,
  isMobile: false,
  hasUnreadMessages: false,
  unreadCount: 0,
})

const emit = defineEmits<IChatTriggerEmits>()

const chatbot = useChatbot()

const contrastColor = computed(() => 'hsl(var(--primary-contrast))')

const triggerClasses = computed(() => [
  'fixed w-14 h-14 min-w-[56px] min-h-[56px]',
  'rounded-full p-0',
  'shadow-lg transition-all duration-200 ease-in-out',
  'hover:scale-105 hover:shadow-xl active:scale-95',
  'sidd-chat-trigger',
  {
    'scale-80 opacity-0 pointer-events-none': props.isHidden,
    'sidd-pulse-animation': props.hasUnreadMessages,
  },
])

const ariaLabel = computed(() => {
  if (!chatbot.store.isInitialized) {
    return `Loading ${chatbot.store.brand?.name} chat...`
  }
  if (props.isOpen) {
    return `Close ${chatbot.store.brand?.name} chat`
  }
  if (props.hasUnreadMessages) {
    return `Open ${chatbot.store.brand?.name} chat - ${props.unreadCount} unread messages`
  }
  return `Open ${chatbot.store.brand?.name} chat`
})

const handleClick = (event: Event) => {
  event.preventDefault()
  event.stopPropagation()
  emit('click')
}

const handleLogoError = () => {
  logger.warn('Failed to load chatbot logo, falling back to icon', undefined, {
    component: 'ChatTrigger',
  })
}
</script>

<style>
.sidd-chat-icon {
  width: 33px !important;
  height: 33px !important;
}

.sidd-chat-trigger {
  z-index: var(--widget-trigger-z-index);
  background-color: hsl(var(--primary)) !important;
  border: 2px solid hsl(var(--background) / 0.2);
  box-shadow:
    0 4px 12px hsl(var(--foreground) / 0.15),
    inset 0 1px 0 hsl(var(--background) / 0.1);
}

.sidd-avatar {
  border-radius: 50%;
  overflow: hidden;
}

.sidd-notification {
  background-color: #ef4444 !important; /* Bright red for visibility */
  color: #ffffff !important;
  border-color: hsl(var(--background));
  font-weight: 600 !important;
  line-height: 1 !important;
}

.sidd-pulse-animation {
  animation: sidd-pulse 2s infinite;
}

@keyframes sidd-pulse {
  0%,
  100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
}
</style>
